import { UploadStatusCard } from "@/components/application/upload-status/upload-status-card";

/**
 * Upload Status Component Library
 *
 * This page serves as the central component library for all upload status card states.
 *
 * NOTE: This is a demo/development page showing the various states of the UploadStatusCard.
 */
export default function UploadStatusDemoPage() {
    const handleAction = (action: string, state: string) => {
        console.log(`${action} clicked for ${state} state`);
    };

    return (
        <div className="h-full overflow-auto bg-primary">
            <div className="container mx-auto max-w-4xl p-6">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="mb-2 text-display-xs font-semibold text-primary">Upload Status Component Library</h1>
                    <p className="mb-6 text-md text-tertiary">Visual demonstrations of all upload and processing states.</p>
                </div>

                {/* Demo Sections */}
                <div className="space-y-8">
                    {/* Uploading State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Uploading</h2>
                            <p className="text-sm text-tertiary">File is being uploaded to the server</p>
                        </div>
                        <UploadStatusCard
                            filename="invoice-march-2024.pdf"
                            status="uploading"
                            progress={0}
                            statusLabel="Uploading..."
                            fileSize={2456789}
                            onCancel={() => handleAction("Cancel", "uploading")}
                        />
                    </div>

                    {/* Queued State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Queued</h2>
                            <p className="text-sm text-tertiary">File uploaded, waiting for processing</p>
                        </div>
                        <UploadStatusCard
                            filename="invoice-march-2024.pdf"
                            status="queued"
                            progress={20}
                            statusLabel="Queued..."
                            pageCount={4}
                            fileSize={2456789}
                            onCancel={() => handleAction("Cancel", "queued")}
                            onViewFile={() => handleAction("View", "queued")}
                        />
                    </div>

                    {/* Processing State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Processing</h2>
                            <p className="text-sm text-tertiary">Document is being processed</p>
                        </div>
                        <UploadStatusCard
                            filename="invoice-march-2024.pdf"
                            status="processing"
                            progress={60}
                            statusLabel="Classifying document..."
                            pageCount={4}
                            documentType="Invoice"
                            document={{
                                id: 1,
                                vendor: "Acme Corp",
                                documentNumber: "INV-2024-001",
                                date: "2024-03-15",
                                amount: "$2,450.00",
                            }}
                            onCancel={() => handleAction("Cancel", "processing")}
                            onViewFile={() => handleAction("View", "processing")}
                        />
                    </div>

                    {/* Success State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Success</h2>
                            <p className="text-sm text-tertiary">Document processed successfully</p>
                        </div>
                        <UploadStatusCard
                            filename="invoice-march-2024.pdf"
                            status="success"
                            progress={100}
                            statusLabel="Ready for export"
                            documentType="Invoice"
                            document={{
                                id: 1,
                                vendor: "Acme Corp",
                                documentNumber: "INV-2024-001",
                                date: "2024-03-15",
                                amount: "$2,450.00",
                            }}
                            onRemove={() => handleAction("Remove", "success")}
                            onViewFile={() => handleAction("View", "success")}
                            onGoToDocument={() => handleAction("GoToDocument", "success")}
                        />
                    </div>

                    {/* Success with Caveats State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Success with Caveats</h2>
                            <p className="text-sm text-tertiary">Document processed but has warnings</p>
                        </div>
                        <UploadStatusCard
                            filename="invoice-march-2024.pdf"
                            status="success-with-caveats"
                            progress={100}
                            statusLabel="Needs review"
                            documentType="Invoice"
                            document={{
                                id: 2,
                                vendor: "RB&W Manufacturing",
                                documentNumber: "INV-2024-002",
                                date: "2024-03-15",
                                amount: "$5,280.00",
                            }}
                            issues={["Price variance on line 3", "Quantity mismatch on line 7"]}
                            varianceInfo={{ amount: "$125.00", direction: "over" }}
                            onRemove={() => handleAction("Remove", "success-with-caveats")}
                            onViewFile={() => handleAction("View", "success-with-caveats")}
                            onGoToDocument={() => handleAction("GoToDocument", "success-with-caveats")}
                        />
                    </div>

                    {/* Exported State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Exported</h2>
                            <p className="text-sm text-tertiary">Document has been exported</p>
                        </div>
                        <UploadStatusCard
                            filename="invoice-march-2024.pdf"
                            status="exported"
                            progress={100}
                            statusLabel="Exported"
                            documentType="Invoice"
                            document={{
                                id: 3,
                                vendor: "Global Industries",
                                documentNumber: "INV-2024-003",
                                date: "2024-03-15",
                                amount: "$8,750.00",
                            }}
                            onViewFile={() => handleAction("View", "exported")}
                        />
                    </div>

                    {/* Error State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Error</h2>
                            <p className="text-sm text-tertiary">Processing failed with an error</p>
                        </div>
                        <UploadStatusCard
                            filename="corrupted-file.pdf"
                            status="error"
                            progress={100}
                            errorCode="ProcOcrFailed"
                            onRemove={() => handleAction("Remove", "error")}
                            onGetHelp={() => handleAction("GetHelp", "error")}
                            onViewFile={() => handleAction("View", "error")}
                        />
                    </div>

                    {/* Duplicate State */}
                    <div className="space-y-4">
                        <div>
                            <h2 className="mb-1 text-lg font-semibold text-primary">Duplicate</h2>
                            <p className="text-sm text-tertiary">File has already been uploaded</p>
                        </div>
                        <UploadStatusCard
                            filename="invoice-duplicate.pdf"
                            status="duplicate"
                            progress={100}
                            duplicateInfo={{
                                originalFilename: "invoice-march-2024.pdf",
                                uploadedDate: "2024-03-10",
                            }}
                            onRemove={() => handleAction("Remove", "duplicate")}
                            onGetHelp={() => handleAction("GetHelp", "duplicate")}
                            onViewFile={() => handleAction("View", "duplicate")}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
